/**
 * ACE AI Size Assistant - Chat Widget
 * Handles chat UI, API communication, and thread management
 */
(function () {
  'use strict';

  // Configuration (set from Liquid template)
  const config = window.aceChatConfig || {};

  // State
  let isLoading = false;
  let lastAutoRequestMeasurementSignature = null;
  let hasWelcomed = false;
  let hideTimeout = null;

  // DOM Elements (cached after init)
  let modal, messagesContainer, inputField, sendButton;

  // Session Storage Keys
  const THREAD_ID_KEY = 'ace_chat_thread_id';

  /**
   * Initialize the chat widget
   */
  function init() {
    if (!config.enabled) return;

    // Cache DOM elements
    modal = document.querySelector('[data-ace-modal]');
    messagesContainer = document.querySelector('[data-ace-messages]');
    inputField = document.querySelector('[data-ace-input]');
    sendButton = document.querySelector('[data-ace-send]');

    if (!modal || !messagesContainer) return;

    // Bind event listeners
    bindEvents();

    console.log('[ACE Chat] Initialized');
  }

  /**
   * Bind all event listeners
   */
  function bindEvents() {
    // Open button(s)
    document.querySelectorAll('[data-ace-open]').forEach(btn => {
      btn.addEventListener('click', openChat);
    });

    // Close button
    document.querySelectorAll('[data-ace-close]').forEach(btn => {
      btn.addEventListener('click', closeChat);
    });

    // Close on overlay click
    if (modal) {
      modal.addEventListener('click', (e) => {
        if (e.target === modal) closeChat();
      });
    }

    // Send button
    if (sendButton) {
      sendButton.addEventListener('click', handleSend);
    }

    // Enter key on input
    if (inputField) {
      inputField.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault();
          handleSend();
        }
      });
    }

    // Escape key to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && modal && modal.classList.contains('is-open')) {
        closeChat();
      }
    });

    window.addEventListener('resize', () => {
      if (modal?.classList.contains('is-open')) {
        messagesContainer.scrollTop = messagesContainer.scrollHeight;
      }
    });
  }

  /**
   * Open the chat modal
   */
  function openChat() {
    if (!modal) return;

    if (hideTimeout) {
      clearTimeout(hideTimeout);
      hideTimeout = null;
    }
    modal.removeAttribute('hidden');
    // Force reflow before adding class for animation
    modal.offsetHeight;
    modal.classList.add('is-open');
    document.body.style.overflow = 'hidden';

    // Focus input
    if (inputField) {
      setTimeout(() => inputField.focus(), 300);
    }

    requestRecommendation();
  }

  /**
   * Close the chat modal
   */
  function closeChat() {
    if (!modal) return;

    if (hideTimeout) clearTimeout(hideTimeout);
    modal.classList.remove('is-open');
    document.body.style.overflow = '';

    hideTimeout = setTimeout(() => {
      if (!modal.classList.contains('is-open')) modal.setAttribute('hidden', '');
      hideTimeout = null;
    }, 300);
  }

  /**
   * Greet visitors without measurements; request a fresh recommendation
   * when the size finder has measurements that have changed.
   */
  function requestRecommendation() {
    if (isLoading) return;

    const measurements = getMeasurements();
    const hasMeasurements = [
      measurements.height_cm, measurements.weight_kg, measurements.chest_cm,
      measurements.bust_cm, measurements.waist_cm, measurements.hips_cm
    ].some(Boolean);

    if (!hasMeasurements) {
      if (!hasWelcomed) {
        renderMessage('assistant', config.translations?.welcome_no_measurements?.replace(/&#39;/g, "'") ||
          "👋 Hi there! I'm Ace and here to help you find the perfect wetsuit size. I can see you haven't filled out our size finder yet.\n\nTo give you the most accurate recommendation, could you share your measurements with me?");
        hasWelcomed = true;
      }
      return;
    }

    const measurementSignature = getMeasurementSignature(measurements);
    const autoMessage = "What size do you recommend for me?";
    const hasMeasurementChanges =
      lastAutoRequestMeasurementSignature !== measurementSignature;

    if (!hasMeasurementChanges) return;

    const displayMessage = buildAutoMessageDisplay(measurements);

    renderMessage('user', displayMessage);
    sendMessage(autoMessage, {
      measurements: measurements,
      onSuccess: () => {
        lastAutoRequestMeasurementSignature = measurementSignature;
      }
    });
  }

  /**
   * Build the visible chat message for the automatic size recommendation request
   */
  function buildAutoMessageDisplay(measurements) {
    const parts = [];

    if (measurements.height_cm) parts.push(`Height: ${measurements.height_cm}`);
    if (measurements.weight_kg) parts.push(`Weight: ${measurements.weight_kg}kg`);
    if (measurements.chest_cm) parts.push(`Chest: ${measurements.chest_cm}`);
    if (measurements.bust_cm) parts.push(`Bust: ${measurements.bust_cm}`);
    if (measurements.waist_cm) parts.push(`Waist: ${measurements.waist_cm}`);
    if (measurements.hips_cm) parts.push(`Hips: ${measurements.hips_cm}`);
    return parts.join(', ');
  }

  /**
   * Build a stable comparison key for the current measurements
   */
  function getMeasurementSignature(measurements) {
    return JSON.stringify({
      gender: measurements.gender || null,
      height_cm: measurements.height_cm || null,
      weight_kg: measurements.weight_kg || null,
      chest_cm: measurements.chest_cm || null,
      bust_cm: measurements.bust_cm || null,
      waist_cm: measurements.waist_cm || null,
      hips_cm: measurements.hips_cm || null
    });
  }

  /**
   * Handle send button click
   */
  function handleSend() {
    if (isLoading) return;

    const userMessage = inputField?.value?.trim();
    if (!userMessage) return;

    // Clear input
    inputField.value = '';

    // Show user message
    renderMessage('user', userMessage);

    // Send to API
    sendMessage(userMessage);
  }

  /**
   * Get measurements from the size finder form
   */
  function getMeasurements() {
    const isInchMode = document.querySelector('.unit.active')?.textContent?.includes('INCH');
    const gender = config.productTag === 'women' ? 'female' : 'male';

    let measurements = {
      gender: gender,
      height_cm: null,
      weight_kg: null,
      chest_cm: null,
      bust_cm: null,
      waist_cm: null,
      hips_cm: null
    };

    if (isInchMode) {
      // Convert from imperial
      const heightFt = parseFloat(document.getElementById('input_height_ft')?.value) || 0;
      const heightIn = parseFloat(document.getElementById('input_height_inch')?.value) || 0;
      const weightLbs = parseFloat(document.getElementById('input_weight_lbs')?.value) || null;
      const chestIn = parseFloat(document.getElementById('input_chest_inch')?.value) || null;
      const bustIn = parseFloat(document.getElementById('input_bust_inch')?.value) || null;
      const waistIn = parseFloat(document.getElementById('input_waist_inch')?.value) || null;
      const hipsIn = parseFloat(document.getElementById('input_hips_inch')?.value) || null;

      // Convert to metric
      if (heightFt || heightIn) {
        measurements.height_cm = Math.round((heightFt * 30.48) + (heightIn * 2.54));
      }
      if (weightLbs) measurements.weight_kg = Math.round(weightLbs * 0.453592);
      if (chestIn) measurements.chest_cm = Math.round(chestIn * 2.54);
      if (bustIn) measurements.bust_cm = Math.round(bustIn * 2.54);
      if (waistIn) measurements.waist_cm = Math.round(waistIn * 2.54);
      if (hipsIn) measurements.hips_cm = Math.round(hipsIn * 2.54);
    } else {
      // Already in metric
      measurements.height_cm = parseFloat(document.getElementById('input_height_cm')?.value) || null;
      measurements.weight_kg = parseFloat(document.getElementById('input_weight_kg')?.value) || null;
      measurements.chest_cm = parseFloat(document.getElementById('input_chest_cm')?.value) || null;
      measurements.bust_cm = parseFloat(document.getElementById('input_bust_cm')?.value) || null;
      measurements.waist_cm = parseFloat(document.getElementById('input_waist_cm')?.value) || null;
      measurements.hips_cm = parseFloat(document.getElementById('input_hips_cm')?.value) || null;
    }

    return measurements;
  }

  /**
   * Send message to API
   */
  async function sendMessage(userMessage, options = {}) {
    if (isLoading) return;

    isLoading = true;
    showLoading();
    setSendButtonState(false);

    const measurements = options.measurements || getMeasurements();
    const threadId = getThreadId();

    const payload = {
      gender: measurements.gender,
      height_cm: measurements.height_cm,
      weight_kg: measurements.weight_kg,
      chest_cm: measurements.chest_cm,
      bust_cm: measurements.bust_cm,
      waist_cm: measurements.waist_cm,
      hips_cm: measurements.hips_cm,
      user_message: userMessage,
      thread_id: threadId,
      include_debug: false
    };

    try {
      const response = await fetch(config.endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Client-Token': config.clientToken
        },
        body: JSON.stringify(payload)
      });

      // Check if response is OK before parsing
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      hideLoading();

      if (data.status === 'success') {
        // Save thread ID
        if (data.thread_id) {
          setThreadId(data.thread_id);
        }

        if (typeof options.onSuccess === 'function') {
          options.onSuccess();
        }

        // Show assistant message
        renderMessage('assistant', data.message);
      } else if (data.status === 'error') {
        // Check if thread expired
        if (data.message?.includes('Thread ID not found') || data.message?.includes('expired')) {
          // Clear thread and retry once
          clearThreadId();
          console.log('[ACE Chat] Thread expired, retrying with new thread');

          // Retry without thread_id
          payload.thread_id = null;
          const retryResponse = await fetch(config.endpoint, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Client-Token': config.clientToken
            },
            body: JSON.stringify(payload)
          });

          if (!retryResponse.ok) {
            throw new Error(`HTTP error! status: ${retryResponse.status}`);
          }

          const retryData = await retryResponse.json();

          if (retryData.status === 'success') {
            if (retryData.thread_id) {
              setThreadId(retryData.thread_id);
            }
            if (typeof options.onSuccess === 'function') {
              options.onSuccess();
            }
            renderMessage('assistant', retryData.message);
          } else {
            renderMessage('error', config.translations?.error_generic ||
              'Sorry, something went wrong. Please try again.');
          }
        } else {
          renderMessage('error', data.message || config.translations?.error_generic ||
            'Sorry, something went wrong. Please try again.');
        }
      }
    } catch (error) {
      console.error('[ACE Chat] API Error:', error);
      hideLoading();

      // More specific error message for network/CORS issues
      let errorMessage = config.translations?.error_generic ||
        'Sorry, something went wrong. Please try again.';

      if (error.message?.includes('Failed to fetch') || error.name === 'TypeError') {
        errorMessage = 'Unable to connect to ACE. Please check your internet connection and try again.';
        console.warn('[ACE Chat] Network or CORS error - API may need CORS configuration');
      }

      renderMessage('error', errorMessage);
    } finally {
      isLoading = false;
      setSendButtonState(true);
    }
  }

  /**
   * Render a message in the chat
   */
  function renderMessage(role, text) {
    if (!messagesContainer) return;

    const messageEl = document.createElement('div');
    messageEl.className = `ace-message ace-message--${role}`;
    messageEl.textContent = text;

    messagesContainer.appendChild(messageEl);

    // Scroll to bottom
    messagesContainer.scrollTop = messagesContainer.scrollHeight;
  }

  /**
   * Show loading indicator
   */
  function showLoading() {
    if (!messagesContainer) return;

    const loadingEl = document.createElement('div');
    loadingEl.className = 'ace-message ace-message--loading';
    loadingEl.setAttribute('data-ace-loading', '');
    loadingEl.textContent = config.translations?.loading || 'Ace is thinking...';

    messagesContainer.appendChild(loadingEl);
    messagesContainer.scrollTop = messagesContainer.scrollHeight;
  }

  /**
   * Hide loading indicator
   */
  function hideLoading() {
    const loadingEl = document.querySelector('[data-ace-loading]');
    if (loadingEl) {
      loadingEl.remove();
    }
  }

  /**
   * Set send button state
   */
  function setSendButtonState(enabled) {
    if (sendButton) {
      sendButton.disabled = !enabled;
    }
  }

  /**
   * Get thread ID from session storage
   * Returns null in test mode to force new conversation each time
   */
  function getThreadId() {
    // In test mode, always return null to start fresh conversation
    if (config.testMode) {
      return null;
    }

    try {
      return sessionStorage.getItem(THREAD_ID_KEY) || null;
    } catch (e) {
      return null;
    }
  }

  /**
   * Set thread ID in session storage
   */
  function setThreadId(threadId) {
    try {
      sessionStorage.setItem(THREAD_ID_KEY, threadId);
    } catch (e) {
      console.warn('[ACE Chat] Could not save thread ID to session storage');
    }
  }

  /**
   * Clear thread ID from session storage
   */
  function clearThreadId() {
    try {
      sessionStorage.removeItem(THREAD_ID_KEY);
    } catch (e) {
      // Ignore
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
